import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon } from '@heroicons/react/24/solid';

export default function SmartCalendarFeature() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-pri-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16">
        {/* Hero section */}
        <section className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm mb-4">
                  <CalendarIcon className="h-4 w-4 text-mint-green mr-2" />
                  <span className="text-white text-xs font-medium">Feature Spotlight</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-soft-pink">
                  Smart Calendar
                </h1>
                
                <div className="h-1 w-20 bg-mint-green mb-6"></div>
                
                <p className="text-lg sm:text-xl text-pri-gray/90 mb-8 max-w-xl">
                  Organize your day and share it with friends and family. Our Smart Calendar is designed to make scheduling effortless and collaborative.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img 
                  src={`${process.env.REACT_APP_CDN_URL}/illustrations/calendar.png`}
                  alt="Smart Calendar Interface" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pri-dark to-transparent opacity-30"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why we built this section */}
        <section className="py-16 bg-gradient-to-b from-pri-dark to-pri-dark/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-soft-pink">Why We Built This?</h2>
                <div className="h-1 w-20 bg-mint-green mx-auto mt-4 mb-6"></div>
              </div>
              
              <div className="bg-gradient-to-br from-pri-dark/90 to-sec-dark/80 rounded-2xl p-8 border border-white/10 shadow-xl">
                <p className="text-pri-gray text-lg leading-relaxed mb-6">
                  Have you ever tried to coordinate schedules with family members who use different devices? 
                  It can be incredibly frustrating when some use iPhones, others use Android, and perhaps someone prefers their laptop.
                </p>
                
                <p className="text-pri-gray text-lg leading-relaxed mb-6">
                  <span className="text-soft-pink font-medium">Sharing calendars across different platforms like iOS, Android, and web is unnecessarily difficult.</span> 
                  Compatibility issues, sync problems, and complicated sharing procedures often lead to missed appointments and scheduling conflicts.
                </p>
                
                <p className="text-pri-gray text-lg leading-relaxed">
                  We built the Jingleez Smart Calendar to solve this problem—a platform where users can access their calendar from 
                  <span className="text-mint-green font-medium"> anywhere, on any device,</span> and easily share their schedules with family and friends. 
                  Our goal is to help you and your loved ones stay on top of your schedules, regardless of which devices you prefer to use.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Key benefits section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Key Benefits
            </motion.h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: "Seamless Sharing",
                  description: "Share your schedule with family and friends, and coordinate plans effortlessly."
                },
                {
                  title: "Smart Scheduling",
                  description: "Our AI-powered scheduling system learns your preferences and suggests optimal times for your activities."
                },
                {
                  title: "Custom Reminders",
                  description: "Set personalized reminders with flexible notification options to stay on top of your day."
                },
                {
                  title: "Cross Platform Access",
                  description: "Access your calendar from any device - iOS, Android, or web - with perfect synchronization everywhere."
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 p-6 rounded-xl border border-white/10 relative"
                >
                  {(benefit.title === "Smart Scheduling" || benefit.title === "Custom Reminders" || benefit.title === "Cross Platform Access") && (
                    <div className="absolute -top-3 -right-3 bg-soft-pink text-pri-dark text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                      Coming Soon
                    </div>
                  )}
                  <h3 className="text-xl font-semibold mb-4 text-mint-green">{benefit.title}</h3>
                  <p className="text-pri-gray">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works section */}
        <section className="py-16 bg-gradient-to-b from-pri-dark to-sec-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              How It Works
            </motion.h2>

            <div className="flex flex-col items-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full"
              >
                <div className="space-y-12">
                  {/* Step 1 with Illustration */}
                  <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20">
                    <div className="p-6">
                      <div className="flex items-start mb-6 justify-start">
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                          01
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-semibold text-soft-pink">Create Events</h3>
                          <p className="text-pri-gray mt-1">Easily add events with detailed information.</p>
                        </div>
                      </div>
                      
                      {/* Calendar Event Creation Vector Illustration */}
                      <motion.div 
                        className="mt-4 flex justify-center"
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                      >
                        <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-6 shadow-lg border border-white/10 overflow-hidden">
                          {/* Decorative elements */}
                          <div className="absolute top-0 right-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -mr-16 -mt-16"></div>
                          <div className="absolute bottom-0 left-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -ml-10 -mb-10"></div>
                          
                          {/* Header */}
                          <div className="flex justify-between items-center mb-5">
                            <h3 className="text-white text-lg font-medium">Create New Event</h3>
                            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                              <span className="text-white">×</span>
                            </div>
                          </div>
                          
                          {/* Form representation */}
                          <div className="space-y-5">
                            {/* Title Field */}
                            <div>
                              <div className="text-white/70 text-sm mb-2">Event Title</div>
                              <div className="h-10 rounded-lg bg-white/10 animate-pulse"></div>
                            </div>
                            
                            {/* Time Section */}
                            <div>
                              <div className="flex items-center mb-2">
                                <div className="w-4 h-4 rounded-full bg-soft-pink/70 mr-2"></div>
                                <div className="text-white/70 text-sm">Time & Duration</div>
                              </div>
                              <div className="flex gap-3">
                                <div className="h-9 flex-1 rounded-lg bg-white/10"></div>
                                <div className="h-9 w-20 rounded-lg bg-white/10"></div>
                                <div className="h-9 w-20 rounded-lg bg-white/10"></div>
                              </div>
                              <div className="h-7 w-40 mt-2 rounded-lg bg-white/5 flex items-center px-3">
                                <span className="text-white/50 text-xs">Ends at: 11:00 AM</span>
                              </div>
                            </div>
                            
                            {/* Location */}
                            <div>
                              <div className="flex items-center mb-2">
                                <div className="w-4 h-4 rounded-full bg-mint-green/70 mr-2"></div>
                                <div className="text-white/70 text-sm">Location</div>
                              </div>
                              <div className="h-9 rounded-lg bg-white/10"></div>
                            </div>
                            
                            {/* Description */}
                            <div>
                              <div className="text-white/70 text-sm mb-2">Description</div>
                              <div className="h-24 rounded-lg bg-white/10"></div>
                            </div>
                            
                            {/* Color Selection */}
                            <div>
                              <div className="text-white/70 text-sm mb-3">Color</div>
                              <div className="flex gap-2">
                                {[
                                  "bg-red-400", 
                                  "bg-blue-400", 
                                  "bg-green-400", 
                                  "bg-purple-400", 
                                  "bg-yellow-400", 
                                  "bg-pink-400",
                                  "bg-teal-400",
                                  "bg-blue-300"
                                ].map((color, idx) => (
                                  <div 
                                    key={idx} 
                                    className={`w-6 h-6 rounded-full ${color} ${idx === 3 ? 'ring-2 ring-white' : ''}`} 
                                  ></div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 mt-6">
                              <div className="h-9 w-20 rounded-full bg-white/10 flex items-center justify-center">
                                <span className="text-white/70 text-sm">Cancel</span>
                              </div>
                              <div className="h-9 w-28 rounded-full bg-gradient-to-r from-soft-pink to-mint-green flex items-center justify-center">
                                <span className="text-white text-sm font-medium">Create Event</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Other Steps */}
                  {[
                    {
                      step: "02",
                      title: "Invite Others",
                      description: "Send invitations to friends and family members directly from the calendar."
                    },
                    {
                      step: "03", 
                      title: "Get Smart Suggestions",
                      description: "Receive AI-powered suggestions for optimal scheduling based on participants' availability.",
                      comingSoon: true
                    },
                    {
                      step: "04",
                      title: "Get Reminders",
                      description: "Receive timely notifications and reminders for upcoming events.",
                      comingSoon: true
                    }
                  ].map((step, index) => (
                    <div key={index} className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/95 rounded-xl overflow-hidden shadow-lg shadow-soft-pink/20 mb-12">
                      <div className="p-6">
                        <div className="flex items-start mb-6 justify-start relative">
                          <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-mint-green text-pri-dark font-bold">
                            {step.step}
                          </div>
                          <div className="ml-4">
                            <h3 className="text-lg font-semibold text-soft-pink">{step.title}</h3>
                            <p className="text-pri-gray mt-1">{step.description}</p>
                          </div>
                          {step.comingSoon && (
                            <div className="absolute -top-3 -right-3 bg-soft-pink text-pri-dark text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                              Coming Soon
                            </div>
                          )}
                        </div>
                        
                        {/* Illustration for Invite Others step */}
                        {step.title === "Invite Others" && (
                          <motion.div 
                            className="mt-4 flex justify-center"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                          >
                            <div className="relative w-full max-w-md bg-pri-dark/60 rounded-xl p-6 shadow-lg border border-white/10 overflow-hidden">
                              {/* Decorative elements */}
                              <div className="absolute top-0 left-0 w-32 h-32 bg-soft-pink/10 rounded-full blur-xl -ml-16 -mt-16"></div>
                              <div className="absolute bottom-0 right-0 w-24 h-24 bg-mint-green/10 rounded-full blur-xl -mr-10 -mb-10"></div>
                              
                              {/* Header */}
                              <div className="flex justify-between items-center mb-5">
                                <h3 className="text-white text-lg font-medium">Share with</h3>
                                <div className="text-white/70">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                              
                              {/* Filter buttons */}
                              <div className="flex gap-2 mb-6">
                                {[
                                  { name: "Family", active: false },
                                  { name: "Friend", active: false },
                                  { name: "Other", active: false },
                                  { name: "All", active: true }
                                ].map((filter, idx) => (
                                  <div 
                                    key={idx} 
                                    className={`px-4 py-2 rounded-full text-sm ${filter.active ? 'bg-white/20 text-white' : 'bg-white/10 text-white/70'}`}
                                  >
                                    {filter.name}
                                  </div>
                                ))}
                              </div>
                              
                              {/* People list */}
                              <div className="space-y-3">
                                {[
                                  { name: "John Doe", selected: true },
                                  { name: "Jane Doe", selected: false },
                                  { name: "John Smith", selected: false }
                                ].map((person, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-gray-400/30 rounded-full flex items-center justify-center text-white text-xs mr-3">
                                        {person.name.split(' ').map(n => n[0]).join('')}
                                      </div>
                                      <span className="text-white">{person.name}</span>
                                    </div>
                                    <div className={`w-6 h-6 rounded-full ${person.selected ? 'bg-mint-green flex items-center justify-center' : 'border border-white/30'}`}>
                                      {person.selected && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-pri-dark" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Action buttons */}
                              <div className="flex justify-end gap-3 mt-6">
                                <div className="px-5 py-2 rounded-full bg-white/10 text-white/70 text-sm">
                                  Cancel
                                </div>
                                <div className="px-5 py-2 rounded-full bg-gradient-to-r from-soft-pink to-mint-green text-white text-sm">
                                  Create Event
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-soft-pink/20 to-mint-green/20 rounded-2xl p-8 md:p-12 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
              <p className="text-lg text-pri-gray mb-8 max-w-2xl mx-auto">
                Join thousands of users who are already experiencing the power of Smart Calendar
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center bg-mint-green text-pri-dark px-8 py-3 rounded-full font-semibold hover:bg-mint-green/90 transition-colors"
              >
                Sign Up Now
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-pri-dark py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <img
                className="h-8 w-auto"
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                alt="Jingleez"
              />
            </div>
            <div className="flex space-x-8">
              <Link to="/" className="text-pri-gray hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-pri-gray hover:text-white transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-pri-gray hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-white/10 text-center">
            <p className="text-pri-gray text-sm">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
} 